import { useState, useEffect, useRef, useCallback } from 'react';
import { TextField } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { Atom } from "react-loading-indicators";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faArrowLeft, faArrowRight, faCircleRight, faLayerGroup, faWandMagicSparkles, faShieldAlt, faX, faStar, faLock, faShieldHalved, faBrain } from '@fortawesome/free-solid-svg-icons';
import BotChatStep from '../components/BotChatStep.js';
import BackButton from '../components/BackButton.js';
import { useNavigate } from "react-router-dom";
import { saveInteraction } from "../components/Interaction.js";
import './LandingPageRegister.css';
import '../components/chatModal.css'
import '../components/widgetPreview.css'
import '../components/widgetCustomizationTab.css'
import { SUPPORTED_LANGUAGE_CODES } from "../translations.js";
import { navigateExternal } from '../utils/navigation.js';

export default function LandingPageRegister ({ onSetupComplete }) {
  const [wsFailed, setWsFailed] = useState(false);
  const completedRef = useRef(false);
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [err, setErr] = useState('');
  const [url, setUrl] = useState('');
  const [pct, setPct] = useState(0);
  const [bot, setBot] = useState(null);
  const [uid, setUid] = useState(null);
  const [jwtToken, setJwtToken] = useState("")
  const [stripePriceId, setStripePriceId] = useState("")
  const [subscriptionPlanID, setSubscriptionPlanID] = useState("")
  const [isTraining, setIsTraining] = useState(false);
  const plans = ["smart", "boost", "ultimo"];
  const [activePlanIdx, setActivePlanIdx] = useState(0);
  const planWrapperRef = useRef(null);
  const planCardRefs = useRef([]);
  const [userEmail, setUserEmail] = useState("");
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const navigate = useNavigate();
  const defaultWidgetStyles = {
    themeColor: "#000000ff",
    headerColor: "#ffffff",
    hoverColor: "#232323ff",
    pulsing: true,
    widgetHorizontalAlignment: "right",
    widgetSize: 60,
    widgetBorderRadius: 50,
  };
  const [widgetStyles, setWidgetStyles] = useState(defaultWidgetStyles);

  useEffect(() => {
    if (!uid) return;

    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) throw new Error("WebSocket URL missing");
      const url = raw.replace(/^http/, "ws");
      ws = new WebSocket(url);
    } catch (err) {
      console.error("WebSocket init failed:", err);
      setWsFailed(true);
      return;
    }

    ws.addEventListener("open", () => {
      ws.send(JSON.stringify({ type: "init", user_id: uid, token: localStorage.getItem('token') }));
    });

    ws.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (bot && msg.bot_id && msg.bot_id !== bot) return;

        if (msg.percentage != null) setPct(msg.percentage);

        if (msg.status === "completed") {
          (async () => {
            await fetchWidgetConfiguration(bot);
            setStep(2);
            setIsTraining(false);
            completedRef.current = true;
          })();
        }

        if (msg.status === "failed") {
          setErr(t("onboard_bot_creation_failed"));
          setIsTraining(false);
          setPct(0);
          setBot(null);
        }
      } catch (e) {
        console.error("WebSocket message parse error", e);
      }
    });

    ws.addEventListener("error", () => {
      if (!completedRef.current) setWsFailed(true);
    });

    return () => ws.close();
  }, [uid, bot]);

  useEffect(() => {
    if (!wsFailed || !bot) return;

    async function pollOnce() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${encodeURIComponent(bot)}`
        );
        if (!res.ok) throw new Error("status fetch failed");
        const data = await res.json();

        if (data.creation_percentage != null) setPct(data.creation_percentage);

        if (data.creation_percentage >= 100) {
          await fetchWidgetConfiguration(bot);
          setStep(2);
          setIsTraining(false);
          clearInterval(timerId);
        }

        if (data.status === "failed") {
          setErr(t("onboard_bot_creation_failed"));
          setIsTraining(false);
          setPct(0);
          setBot(null);
        }
      } catch (e) {
        console.error("Polling error:", e);
        setErr(t("onboard_progress_unavailable"));
        clearInterval(timerId);
      }
    }

    const timerId = setInterval(pollOnce, 1000);
    pollOnce();

    return () => clearInterval(timerId);
  }, [wsFailed, bot]);

  useEffect(() => {
    if (step !== 2) return;
    const wrapper = planWrapperRef.current;
    const cards   = planCardRefs.current;
    if (!wrapper || cards.length === 0) return;
    const GAP = 20;
    const updateActiveIdx = () => {
      const cardWidth = cards[0].offsetWidth;
      const idx = Math.round(wrapper.scrollLeft / (cardWidth + GAP));
      setActivePlanIdx(Math.min(cards.length - 1, Math.max(0, idx)));
    };

    updateActiveIdx();
    wrapper.addEventListener("scroll", updateActiveIdx, { passive: true });

    return () => wrapper.removeEventListener("scroll", updateActiveIdx);
  }, [step]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const preUrl = params.get('url');

    const urlLangParam = Array.from(params.entries()).find(([key]) =>
      SUPPORTED_LANGUAGE_CODES.includes(key)
    );
    if (urlLangParam) {
      const lang = urlLangParam[0];
      i18n.changeLanguage(lang);
      localStorage.setItem("language", lang);
    }

    if (preUrl) {
      setUrl(preUrl);
      saveInteraction({ action: `lawyer_${preUrl}`});
    }

    if (sessionId) {
      verifyPayment(sessionId);
    }
  }, []);

  const fetchWidgetConfiguration = useCallback(
    async (botId) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${botId}`
        );
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();

        const fetchedStyles = {
          themeColor: data.theme_color,
          hoverColor: data.button_hover_color,
          headerColor: data.header_font_color,
          pulsing: data.pulsing,
          widgetHorizontalAlignment: data.widget_horizontal_alignment,
          widgetBorderRadius: data.widget_border_radius ?? 50,
          widgetSize: data.widget_size ?? 70
        };

        setWidgetStyles(fetchedStyles);
      } catch (err) {
        console.error("Error fetching widget configuration:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  async function verifyPayment(sessionId) {
    setPageLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}stripe/validate-session?session_id=${sessionId}`);
      const data = await res.json();

      if (data.status === "valid") {
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'free_trial_started' });
        }

        setUid(data.user_id);
        setJwtToken(data.token);

        localStorage.setItem("user_id", data.user_id);
        localStorage.setItem("token", data.token);

        if (onSetupComplete) onSetupComplete(data.token, data.user_id);

        setStep(6);
      } else if (data.status === "unpaid") {
        setErr(t("onboard_payment_unverified"));
      } else {
        setErr(t("onboard_payment_verify_error"));
      }
    } catch (e) {
      console.error("Payment verification failed", e);
      setErr(t("onboard_payment_verify_error"));
    } finally {
      setPageLoading(false);
    }
  }

  function extractDomainName(inputUrl) {
    try {
      const prefixedUrl = inputUrl.startsWith('http') ? inputUrl : 'https://' + inputUrl;
      const parsedUrl = new URL(prefixedUrl);

      let host = parsedUrl.hostname;
      if (host.startsWith('www.')) host = host.slice(4);
      const parts = host.split('.');

      if (parts.length >= 2) {
        return parts[parts.length - 2];
      }

      return host;
    } catch {
      return 'bot';
    }
  }

  async function createBot() {
    setWsFailed(false);
    saveInteraction({ action: `bot_creation_started_${url}`});
    setErr('');
    if (!url.trim()) {
      setErr(t('onboard_enter_website_url'));
      return;
    }
    const result = await isValidUrlRemote(url);
    if (!result.valid) {
      setErr(result.reason || t('onboard_invalid_site_url'));
      return;
    }

    try {
      setIsTraining(true);

      function generateComplexEmail () {
        const ts   = Date.now();
        const rnd  = Math.floor(Math.random()*1e10)
                      .toString()
                      .padStart(10, '0');

        const localPart = `landing_${ts}_${rnd}`.slice(0, 64);
        return `${localPart}@test.com`;
      }

      const email = generateComplexEmail();

      const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password: "Admin_2025!not_activated",
          given_name: "User",
          family_name: "",
          language: "English",
          send_email: false
        }),
      });

      if (!r.ok) {
        const errText = await r.text();
        throw new Error(`Registration failed: ${errText}`);
      }

      const j = await r.json();
      const userId = j.user_id;
      const token = j.token;
      setUid(userId);
      setJwtToken(token);

      const domainBotName = extractDomainName(url);

      const fd = new FormData();
      fd.append('url', url);
      fd.append('bot_name', domainBotName);
      fd.append('language', 'English');
      fd.append('user_id', userId);
      fd.append('send_email_to_user', false);
      fd.append('javascript_rendering', false);

      try {
        const r2 = await fetch(`${process.env.REACT_APP_API_BASE_URL}create_chatbot`, {
          method: 'POST',
          body: fd,
        });

        if (!r2.ok) {
          const errText = await r2.text();
          throw new Error(`Bot creation failed: ${errText}`);
        }

        const j2 = await r2.json();
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'bot_creation_started' });
        }
        setBot(j2.bot_id);
        localStorage.setItem("botId", j2.bot_id);
      } catch (error) {
        console.error(error);
        setErr(t('onboard_create_failed_short'));
        setIsTraining(false);
      }

    } catch (error) {
      console.error(error);
      setErr(t('onboard_create_failed_short'));
      setIsTraining(false);
    }
  }

  async function isValidUrlRemote(url) {
    if (!url) return { valid: false, reason: "No URL provided" };

    const trimmed = url.trim();
    const encodedUrl = encodeURIComponent(trimmed);

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}url/check?url=${encodedUrl}`);
      if (!response.ok) {
        return { valid: false, reason: "API request failed" };
      }

      const result = await response.json();

      if (result.reachable !== true) {
        return { valid: false, reason: "URL not reachable" };
      }

      if (result.is_valid !== "yes") {
        return { valid: false, reason: result.decline_reason || "Invalid business URL" };
      }

      if (window.rdt) {
        window.rdt('track', 'Lead');
      }

      return { valid: true };
    } catch (error) {
      console.error("Error checking URL validity:", error);
      return { valid: false, reason: "Unexpected error" };
    } finally {
      setLoading(false);
    }
  }

  const handleStyleChange = (key, value) => {
    setWidgetStyles((prevStyles) => ({ ...prevStyles, [key]: value }));
  };

  async function saveWidgetStyles() {
    setErr('');
    setLoading(true);

    try {
      // Step 1: Update widget configuration
      const updates = {
        theme_color: widgetStyles.themeColor,
        header_font_color: widgetStyles.headerColor,
        button_hover_color: widgetStyles.hoverColor,
        pulsing: widgetStyles.pulsing,
        widget_horizontal_alignment: widgetStyles.widgetHorizontalAlignment,
        widget_size: widgetStyles.widgetSize,
        widget_border_radius: widgetStyles.widgetBorderRadius,
      };

      const configResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${encodeURIComponent(bot)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        }
      );

      if (!configResponse.ok) {
        throw new Error('Widget configuration failed');
      }

      // Step 2: Proceed to next step
      setStep(3);
    } catch (e) {
      setErr(t('onboard_integration_failed'));
      setStep(2);
    } finally {
      setLoading(false);
    }
  }

  const updateUser = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      setErr(t("onboard_invalid_email"));
      return;
    }

    if (pw.length < 8) {
      setErr(t("password_too_short"));
      return;
    }

    if (pw !== pw2) {
      setErr(t("passwords_do_not_match"));
      return;
    }

    const updatePayload = {
      email: userEmail,
      hashed_password: pw,
    };

    try {
      setLoading(true);
      setErr(null);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}users/${uid}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`User update failed: ${errorText}`);
      }

      // Setting a password revokes every other session (server-side
      // token_version bump); the response carries a fresh token for THIS
      // session so the requests below stay authenticated.
      try {
        const updated = await response.json();
        if (updated && typeof updated.token === "string") {
          localStorage.setItem("token", updated.token);
        }
      } catch (e) {
        // no JSON body — fall through; the next 401 re-logs in
      }

      const warmLeadPayload = {
        email: [userEmail],
      };

      const warmLeadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${bot}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(warmLeadPayload),
      });

      if (!warmLeadResponse.ok) {
        const errorText = await warmLeadResponse.text();
        throw new Error(`Warm lead update failed: ${errorText}`);
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot_id: bot,
          price_id: stripePriceId,
          subscription_plan_id: subscriptionPlanID,
          success_url: "https://portal.ultimo-bots.com/create-chatbot",
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Checkout session creation failed: ${errorText}`);
      }

      if (window.dataLayer) {
        window.dataLayer.push({ event: 'checkout_session_started' });
      }

      const data = await res.json();
      navigateExternal(data.checkout_url);

    } catch (err) {
      console.error("Failed:", err);
      setErr(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    setPct(0);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}users/${uid}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete user. Status: ${response.status}`);
      }

    } catch (err) {
      console.error('Error deleting user:', err);
    } finally {
      setLoading(false);
    }
  };

  function TrustSlide({ onNext }) {
    const reviews = [
      {
        txt: t('onboard_review_gaby'),
        author: 'Gaby',
        role: 'Founder - Schokidates',
        link: 'https://schokidates.com',
      },
      {
        txt: t('onboard_review_rebecca'),
        author: 'Rebecca',
        role: 'Founder - Gyoo',
        link: 'https://www.gyoo.fr/',
      },
      {
        txt: t('onboard_review_erin'),
        author: 'Erin',
        role: 'Executive Director - LACAE',
        link: 'https://www.la-cae.org/',
      },
      {
        txt: t('onboard_review_marc'),
        author: 'Marc',
        role: 'Co-founder - Egort & Partners',
        link: 'https://www.epfsllc.com/',
      },
      {
        txt: t('onboard_review_joe'),
        author: 'Joe',
        role: 'CEO - Ecoshield',
        link: 'https://www.ecoshield.com.au/',
      },
      {
        txt: t('onboard_review_giovanni'),
        author: 'Giovanni',
        role: 'Co-founder - Furry Friends',
        link: 'https://www.furryfriendsdoggiedaycare.com/',
      },
      {
        txt: t('onboard_review_dougi'),
        author: 'Dougi',
        role: 'Owner - Reinvigr8 Gym',
        link: 'https://www.reinvigr8.com.au/',
      },
      {
        txt: t('onboard_review_lena'),
        author: 'Lena',
        role: 'Marketing Lead - Thrive Communities',
        link: 'https://www.thrivelivin.com/',
      },
      {
        txt: t('onboard_review_tom'),
        author: 'Tom',
        role: 'Founder - Suite Chalets Tremblant',
        link: 'https://www.suitechaletstremblant.com/',
      },
    ];

    const [idx, setIdx] = useState(0);
    const timerRef = useRef(null);
    const touchStartX = useRef(0);
    const touchDeltaX = useRef(0);

    const goRelative = useCallback(
      delta => {
        setIdx(p => (p + delta + reviews.length) % reviews.length);
        resetTimer();
      },
      [reviews.length]
    );

    const goTo = useCallback(
      i => {
        setIdx(i);
        resetTimer();
      },
      []
    );

    const startTimer = () =>
      (timerRef.current = setInterval(
        () => setIdx(i => (i + 1) % reviews.length),
        5000
      ));

    const resetTimer = () => {
      clearInterval(timerRef.current);
      startTimer();
    };

    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchDeltaX.current = 0;
      clearInterval(timerRef.current);
    };

    const handleTouchMove = (e) => {
      touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    };

    const handleTouchEnd = () => {
      if (Math.abs(touchDeltaX.current) > 50) {
        if (touchDeltaX.current < 0) {
          goRelative(+1);
        } else {
          goRelative(-1);
        }
      } else {
        resetTimer();
      }
    };

    useEffect(() => {
      startTimer();
      return () => clearInterval(timerRef.current);
    }, []);

    return (
      <div className="card-button-wrapper">
        <div className="card trust-slide">
          <h2 style={{ margin: '0' }}>
            <Trans
              i18nKey="onboard_trust_headline"
              components={{
                count: <span style={{ color: '#5616ea' }} />,
                brand: <span style={{ color: '#5616ea' }} />,
              }}
            />
          </h2>

          <div
            className="carousel"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="carousel-track"
              style={{ transform: `translateX(-${idx * 100}%)` }}
            >
              {reviews.map((r, i) => (
                <div className="slide" key={i}>
                  <div className="slide-content review-card">
                    <p className="review-text"><span style={{ color: '#5616ea', fontWeight: '800', fontSize: '22px' }}>“</span>{r.txt}<span style={{ color: '#5616ea', fontWeight: '800', fontSize: '22px' }}>”</span></p>
                    <div className='rating-stars'>
                      <FontAwesomeIcon icon={faStar}></FontAwesomeIcon><FontAwesomeIcon icon={faStar}></FontAwesomeIcon><FontAwesomeIcon icon={faStar}></FontAwesomeIcon><FontAwesomeIcon icon={faStar}></FontAwesomeIcon><FontAwesomeIcon icon={faStar}></FontAwesomeIcon>
                    </div>
                    <div className="review-footer">
                      <p className="review-author">
                        <strong>{r.author}</strong><br />
                        {r.role}
                      </p>
                      <button
                        className="live-btn"
                        onClick={() => {
                          window.open(r.link, '_blank', 'noopener,noreferrer');
                          saveInteraction({ action: `bot_demo_clicked_${r.link}` });
                        }}
                      >
                        {t('onboard_see_bot_live')}
                      </button>

                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="carousel-nav prev"
              onClick={() => goRelative(-1)}
              aria-label={t('aria_previous_review')}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <button
              type="button"
              className="carousel-nav next"
              onClick={() => goRelative(+1)}
              aria-label={t('aria_next_review')}
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>

          <div className="carousel-dots">
            {reviews.map((_, i) => (
              <span
                key={i}
                className={`dot ${i === idx ? 'active' : ''}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>

          <div className="trust-extras">
            <div className="badges-wrapper">
              <ul className="badges">
                <li><FontAwesomeIcon icon={faShieldAlt} /> {t('onboard_gdpr_ccpa_badge')}</li>
              </ul>
            </div>
          </div>

          <button
            className="btn"
            type="button"
            style={{ alignSelf: 'center' }}
            onClick={() => {
              saveInteraction({ action: 'start_free_clicked' });
              onNext();
            }}
          >
            <div className="portal-button-content">
              {t('onboard_start_free')}
            </div>
          </button>
        </div>
      </div>
    );
  }

  const slides = [
    /* step 0 – trust & social proof */
    <TrustSlide onNext={() => setStep(1)} />,

    /* step 1 – url */
    <div className="card-button-wrapper">
      <form className={`card${isTraining ? ' building' : ''}`} style={{paddingBottom: '0.7rem'}} onSubmit={e=>e.preventDefault()} autoComplete="off">
        <div className='header-back-button'>
          {/* <BackButton step={step} setStep={setStep} /> */}
          <h2>{t("create_your_assistant")}</h2>
        </div>
        <p className="sub" style={{marginBottom: '15px'}}>
          {t("step1_p1_part4")} <strong>{t("website_url")}</strong>:
        </p>
        <FontAwesomeIcon icon={faArrowDown} className='url-arrow-down' />
        <TextField
          fullWidth
          label={t('url')}
          variant="outlined"
          value={url}
          onChange={e => setUrl(e.target.value)}
          margin="none"
          placeholder="https://example.com"
          required
          error={err}
          disabled={loading || isTraining}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#5616ea',
              },
            },
            '& .MuiOutlinedInput-root.Mui-error fieldset': {
              borderColor: '#dc0e0e',
            },
            '& .MuiInputLabel-root': {
              color: 'gray',
              '&.Mui-focused': {
                color: '#5616ea',
              },
              '&.Mui-error': {
                color: '#dc0e0e',
              },
            },
          }}
        />
        <div className='start-website-hint-text'>
          <p><FontAwesomeIcon icon={faBrain} className='shield-icon' /> {t('onboard_hint_learns_business')}</p>
          <p><FontAwesomeIcon icon={faShieldHalved} className='shield-icon' /> {t('onboard_hint_gdpr')}</p>
        </div>
        {isTraining && (
          <div className='chatbot-loading-container'>
            <Atom color="#5616ea" size="medium" speedPlus={'0'} />
            {(() => {
              const rounded = Math.round(pct);
              let label;

              if (rounded <= 5) {
                label = t("training_starting");
              } else if (rounded <= 50) {
                label = t("training_extracting");
              } else if (rounded <= 80) {
                label = t("training_embedding");
              } else {
                label = t("training_finalizing");
              }

              return `${label}: ${rounded}%`;
            })()}
          </div>
        )}
        {err && <div className="err">{err}</div>}
      </form>
      <div>
        <button className="btn" type="button" onClick={createBot} disabled={loading || isTraining}>
          <div className='portal-button-content'>
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            {t("create_assistant")} {loading && <span className="spin" />}
          </div>
        </button>
        <p className='footer-cancel-info'>{t("cancel_anytime")}</p>
      </div>
    </div>,

    /* step 2 – customise */
    <div className='card-button-wrapper'>
      <div className='preview-parent-container'>
        <div className='preview-header-text'>
          <div className='header-back-button'>
            <BackButton step={step} setStep={setStep} onBack={handleDeleteUser} />
            <h2>{t("customize_your_assistant")}</h2>
          </div>
          <p className="sub" style={{marginBottom: "0px"}}>
            {t("onboard_customize_design_desc")}
          </p>
        </div>
        <div className="preview-card">
          <div className='user-settings'>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title" htmlFor="themeColorPicker">{t("theme_color")}</label>
                <input
                  id="themeColorPicker"
                  type="color"
                  value={widgetStyles.themeColor}
                  onChange={(e) => handleStyleChange("themeColor", e.target.value)}
                />
              </div>
            </div>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title">{t("header_font_color")}</label>
                <input
                  type="color"
                  value={widgetStyles.headerColor}
                  onChange={(e) =>
                    handleStyleChange("headerColor", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title">{t("button_hover_color")}</label>
                <input
                  type="color"
                  value={widgetStyles.hoverColor}
                  onChange={(e) => handleStyleChange("hoverColor", e.target.value)}
                />
              </div>
            </div>
            <div className="control-group">
              <div className="color-picker-container">
                <label className="widget-setting-title">{t("enable_pulsing")}</label>
                <div className="radio-buttons">
                  <div className="radio-buttons-content">
                    <input
                      type="radio"
                      id="pulsing-enabled"
                      name="pulsing"
                      value="enabled"
                      checked={widgetStyles.pulsing === true}
                      onChange={() => handleStyleChange("pulsing", true)}
                    />
                    <label htmlFor="pulsing-enabled">{t("enabled")}</label>
                  </div>
                  <div className="radio-buttons-content">
                    <input
                      type="radio"
                      id="pulsing-disabled"
                      name="pulsing"
                      value="disabled"
                      checked={widgetStyles.pulsing === false}
                      onChange={() => handleStyleChange("pulsing", false)}
                    />
                    <label htmlFor="pulsing-disabled">{t("disabled")}</label>
                  </div>
                </div>
              </div>
            </div>
            <div className="control-group">
              <label>
                <div className="border-radius-container">
                  <div className="widget-setting-title">{t("widget_size")}</div>
                  <div>
                    <input
                      type="range"
                      min="40"
                      max="100"
                      value={widgetStyles.widgetSize}
                      onChange={(e) =>
                        handleStyleChange("widgetSize", parseInt(e.target.value, 10))
                      }
                    />
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div class="divider"></div>
          <div className="preview-area">
            <div
              className={`chat-widget-icon
                          ${widgetStyles.pulsing ? "pulsing" : ""}
                          ${widgetStyles.widgetHorizontalAlignment === "left" ? "align-left" : "align-right"}
                          ${widgetStyles.widgetVerticalAlignment === "elevated" ? "elevated" : "bottom"}`}
              style={{
                width:  `${widgetStyles.widgetSize}px`,
                height: `${widgetStyles.widgetSize}px`,
                borderRadius: `${widgetStyles.widgetBorderRadius}%`,
                backgroundColor: widgetStyles.icon ? "transparent" : widgetStyles.themeColor,
                flex: "0 0 auto",
                display: "flex",
              }}
            >
              {widgetStyles.icon ? (
                <img
                  src={widgetStyles.icon}
                  alt="Widget Icon"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: `${widgetStyles.widgetBorderRadius}%`,
                  }}
                />
              ) :
                <div style={{display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "transparent", paddingTop: "3px"}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="65%" viewBox="0 0 24 24" fill="white" class="size-6">
                    <path fill-rule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clip-rule="evenodd" />
                  </svg>
                </div>
              }
            </div>

            <div
              className={`chat-window show
                          ${widgetStyles.widgetHorizontalAlignment === "left" ? "align-left" : "align-right"}
                          ${widgetStyles.widgetVerticalAlignment === "elevated" ? "elevated" : "bottom"}`}
              style={{
                width: "280px",
                height: "380px",
              }}
            >
              <div
                className="chat-header"
                style={{ backgroundColor: widgetStyles.themeColor }}
              >
                <div className="chat-header-title">
                  <div className="logo-message-container">
                    {widgetStyles.logo && (
                      <img
                        src={widgetStyles.logo}
                        alt="Chat Logo"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <span
                      className="onboarding-chat-title"
                      style={{ color: widgetStyles.headerColor }}
                    >
                      {widgetStyles.headerText || t("chat_with_us")}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="onboarding-widget-close-button"
                    style={{ color: widgetStyles.headerColor }}
                    aria-label={t("aria_close_chat")}
                  >
                    <FontAwesomeIcon icon={faX}/>
                  </button>
                </div>
              </div>

              <div className="chat-body">
                <div className="widget-message widget-bot-message">
                  {widgetStyles.welcomeMessage || t("onboard_preview_welcome_msg")}
                </div>

                <div className="loading-dots">
                  <div style={{ background: widgetStyles.themeColor }}></div>
                  <div style={{ background: widgetStyles.themeColor }}></div>
                  <div style={{ background: widgetStyles.themeColor }}></div>
                </div>
              </div>

              <div className="chat-footer">
                <div className="powered-by">
                  <span className="powered-by-text">{t("powered_by_ultimo_bots")}</span>
                </div>

                <div className="input-send-container">
                  <input
                    type="text"
                    className="chat-input"
                    placeholder={t("widget_type_message_placeholder")}
                  />
                  <button
                    type="button"
                    className="onboarding-send-message"
                    style={{
                      backgroundColor: widgetStyles.themeColor,
                      color: widgetStyles.headerColor,
                    }}
                  >
                    <i className="widget-send-icon"><FontAwesomeIcon icon={faArrowUp}/></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .onboarding-send-message:hover {
              background-color: ${widgetStyles.hoverColor} !important;
            }
            .preview-area .loading-dots div {
              background-color: ${widgetStyles.themeColor} !important;
            }
          `}</style>
        </div>
        {err && <div className="err">{err}</div>}
      </div>
      <button
        className="btn"
        disabled={loading}
        onClick={() => {
          saveInteraction({ action: 'test_chatbot_clicked' });
          saveWidgetStyles();
        }}
      >
        <div className="portal-button-content">
          <FontAwesomeIcon icon={faCircleRight} />
          {t("onboard_test_your_agent")} {loading && <span className="spin" />}
        </div>
      </button>
    </div>,

    /* step 3 – test */
    <div className='card-button-wrapper'>
      <div className="chatbot-card">
        <div className='test-assistant-text-container'>
          <div className='header-back-button'>
            <BackButton step={step} setStep={setStep} />
            <h2>{t("test_your_assistant")}</h2>
          </div>
          <p className="sub" style={{marginBottom: '10px'}}>
            {t("test_assistant_desc")}
          </p>
        </div>
        {step >= 2 && (
          <BotChatStep
            bot={bot}
          />
        )}
      </div>
      <button
        className="btn"
        disabled={loading}
        onClick={() => {
          saveInteraction({ action: 'access_portal_clicked' });
          setStep(4);
        }}
        style={{ maxWidth: "260px" }}
      >
        <div className="portal-button-content">
          <FontAwesomeIcon icon={faCircleRight} />
          {t("access_our_portal")} {loading && <span className="spin" />}
        </div>
      </button>
    </div>,

    /* step 4 – choose subscription */
    <div className="subscription-section">
      <div className='header-back-button'>
        <BackButton step={step} setStep={setStep} />
        <h2>{t("choose_your_subscription")}</h2>
      </div>
      <p className="sub">
        {t("plan_intro_part1")} <strong>{t("free_trial_7_days")}</strong>.
      </p>

      <div className="plan-slider-wrapper" ref={planWrapperRef}  >
        <div className="plan-slider">
          <div className="plan-card highlighted" ref={el => (planCardRefs.current[0] = el)}>
            <h3 style={{margin: 0}}>{t("plan_smart")}</h3>
            <div>
              <p className="plan-price">{t("plan_smart_price")}</p>
              <p className="plan-price-month">{t("per_month")}</p>
            </div>
            <ul className="plan-features">
              <li>{t("smart_feature_messages")}</li>
              <li>{t("smart_feature_documents")}</li>
              <li>{t("smart_feature_behavior")}</li>
              <li>{t("smart_feature_warm_lead")}</li>
              <li>{t("smart_feature_rescraping")}</li>
            </ul>

            <button className="btn" disabled={loading} onClick={() => {saveInteraction({ action: 'smart_plan_clicked' }); setStep(5); setStripePriceId("price_1RhFgODR0WHHWTu7d1OFpnRj"); setSubscriptionPlanID(5);}}>
              <div className='portal-button-content'>
                <FontAwesomeIcon icon={faCircleRight} />
                {t("start_free_trial")} {loading && <span className="spin" />}
              </div>
            </button>
          </div>

          <div className="plan-card" ref={el => (planCardRefs.current[1] = el)}>
            <h3 style={{margin: 0}}>{t("plan_boost")}</h3>
            <div>
              <p className="plan-price">{t("plan_boost_price")}</p>
              <p className="plan-price-month">{t("per_month")}</p>
            </div>
            <ul className="plan-features">
              <li>{t("boost_feature_messages")}</li>
              <li>{t("boost_feature_documents")}</li>
              <li>{t("boost_feature_behavior")}</li>
              <li>{t("boost_feature_warm_lead")}</li>
              <li>{t("boost_feature_rescraping")}</li>
            </ul>

            <button className="btn" disabled={loading} onClick={() => {saveInteraction({ action: 'boost_plan_clicked' }); setStep(5); setStripePriceId("price_1RhFgfDR0WHHWTu7XcncK6g6"); setSubscriptionPlanID(6);}}>
              <div className='portal-button-content'>
                <FontAwesomeIcon icon={faCircleRight} />
                {t("start_free_trial")} {loading && <span className="spin" />}
              </div>
            </button>
          </div>

          <div className="plan-card" ref={el => (planCardRefs.current[2] = el)}>
            <h3 style={{margin: 0}}>{t("plan_ultimo")}</h3>
            <div>
              <p className="plan-price">{t("plan_ultimo_price")}</p>
              <p className="plan-price-month">{t("per_month")}</p>
            </div>
            <ul className="plan-features">
              <li>{t("ultimo_feature_messages")}</li>
              <li>{t("ultimo_feature_documents")}</li>
              <li>{t("ultimo_feature_behavior")}</li>
              <li>{t("ultimo_feature_warm_lead")}</li>
              <li>{t("ultimo_feature_rescraping")}</li>
            </ul>

            <button className="btn" disabled={loading} onClick={() => {saveInteraction({ action: 'ultimo_plan_clicked' }); setStep(5); setStripePriceId("price_1RhFhADR0WHHWTu7rgs5eOCQ"); setSubscriptionPlanID(7);}}>
              <div className='portal-button-content'>
                <FontAwesomeIcon icon={faCircleRight} />
                {t("start_free_trial")} {loading && <span className="spin" />}
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="pricing-indicators">
        {plans.map((_, i) => (
          <div
            key={i}
            className={`indicator ${i === activePlanIdx ? "active" : ""}`}
          />
        ))}
      </div>

      <p className="cancel-note">
        {t("cancel_note")}
      </p>
    </div>,

    /* step 5 – password */
    <div className='card-button-wrapper'>
      <form className="card">
        <p className="sub">
          {t("create_account_desc_part1")} <strong>{t("ultimo_bots_portal")}</strong> {t("create_account_desc_part2")}
        </p>
        <input className="register-input-text" type="email" placeholder={t('email')} value={userEmail} onChange={e=>setUserEmail(e.target.value)} required/>
        <input className="register-input-text" type="password" placeholder={t('password')} value={pw} onChange={e=>setPw(e.target.value)} required/>
        <input className="register-input-text" type="password" placeholder={t('confirm_password')} value={pw2} onChange={e=>setPw2(e.target.value)} required/>

        {err && <div className="err">{err}</div>}
      </form>
      <button
        className="btn"
        disabled={loading}
        onClick={() => {
          saveInteraction({ action: 'create_user_clicked' });
          updateUser();
        }}
      >
        {t("continue")} {loading && <span className="spin" />}
      </button>
    </div>,

    /* step 6 – success & next steps */
    <div className="card">
      <h2 className='complete-title'>{t("assistant_activated_title")} <span className='live-highlight'>{t("activated")}</span></h2>

      <div className="after-setup-links">
        <div className='integration-complete-content'>
          <FontAwesomeIcon icon={faLayerGroup} className='chatbot-integrated-icon'/>
          <div>{t("integrate_your_assistant")}</div>
        </div>
        <FontAwesomeIcon icon={faArrowDown} className='url-arrow-down' style={{marginBottom: '0px'}}/>
        <div className="centered-button-wrapper">
          <button
            className="integration-button-link"
            onClick={() => {
              const botId = localStorage.getItem('botId');
              navigate(`/?bot_id=${botId}&showIntegrationModal=1`);
            }}
          >
            <div className='portal-button-content'>
              <FontAwesomeIcon icon={faCircleRight} className='forward-portal-icon'/>
              {t("integrate_to_website")}
            </div>
          </button>
        </div>
      </div>
    </div>
  ];

  if (pageLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="wrapper register-scope">
      <div className="logo-register-site">
        <a href="https://ultimo-bots.com" target="_blank" rel="noopener noreferrer">
          <img src="/ultimo-bots-white.png" alt="logo" />
        </a>
      </div>
      {/* <div className="step-ind">{t("step")} {step+1}/{slides.length}</div> */}
      <div className="steps-wrapper">
        <div className="steps-slider" style={{transform:`translateX(-${step*100}%)`}}>
          {slides.map((c,i)=>(
            <div key={i} className="step-slide">
              {c}
            </div>
          ))}
        </div>
      </div>
      <div className="landing-footer-links">
        <a href="https://ultimo-bots.com/terms" target="_blank" rel="noopener noreferrer">{t("footer_terms")}</a>
        <a href="https://ultimo-bots.com/privacy" target="_blank" rel="noopener noreferrer">{t("footer_privacy_policy")}</a>
        <a href="https://ultimo-bots.com/imprint" target="_blank" rel="noopener noreferrer">{t("footer_imprint")}</a>
      </div>
    </div>
  );
}
